import { account } from "@pinnacle/api";
import { GeneralLoginResponse, UserInfo } from "@pinnacle/types/user.ts";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import * as Sentry from "@sentry/react";

interface UserState {
  user?: UserInfo;
  auth?: GeneralLoginResponse;
  loginStatus?: boolean;
  setUser: (user: UserInfo) => void;
  setAuth: (auth: GeneralLoginResponse) => void;
  refreshLoginStatus: (refreshToken?: boolean) => Promise<boolean>;
  refreshUserInfo: () => Promise<void>;
  clearLoginInfo: () => void;
}

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set, get) => ({
        user: undefined,
        auth: undefined,
        loginStatus: false,
        setUser: (user) => set((prv) => ({ ...prv, user })),
        setAuth: (auth) => set((prv) => ({ ...prv, auth })),
        refreshUserInfo: async () => {
          try {
            if (!get().auth) {
              throw new Error("no auth");
            }
            const userResponse = await account.personInfo();
            set((prv) => ({
              ...prv,
              user: userResponse.data,
            }));
          } catch (e) {
            console.log(e);
          }
        },
        refreshLoginStatus: async (refreshToken = true) => {
          if (refreshToken) {
            let auth = get().auth;
            if (!auth || !auth.refreshToken) {
              const refreshToken = new URLSearchParams(
                window.location.search
              ).get("refreshToken");
              if (refreshToken) {
                auth = {
                  refreshToken,
                  refreshTokenExpireIn: 0,
                  token: "",
                  expireIn: 0,
                };
              }
            }
            if (auth && auth.refreshToken) {
              try {
                const refreshResult = await account.refreshToken({
                  refreshToken: auth.refreshToken,
                });
                set((prv) => ({
                  ...prv,
                  auth: {
                    refreshTokenExpireIn: auth.refreshTokenExpireIn,
                    expireIn: auth.expireIn,
                    token: refreshResult.data.token,
                    refreshToken: refreshResult.data.refreshToken,
                  },
                }));
              } catch (e) {
                console.log(e);
              }
            }
          }
          try {
            if (!get().auth) {
              throw new Error("no auth");
            }
            const userResponse = await account.personInfo();
            set((prv) => ({
              ...prv,
              user: userResponse.data,
              loginStatus: true,
            }));
            Sentry.setUser({
              id: userResponse.data.id,
              username: userResponse.data.nick_name,
            });
          } catch (e) {
            set((prv) => ({
              ...prv,
              user: undefined,
              auth: undefined,
              loginStatus: false,
            }));
            Sentry.setUser(null);
            return false;
          }
          return true;
        },
        clearLoginInfo: () => {
          set((prv) => ({
            ...prv,
            user: undefined,
            auth: undefined,
            loginStatus: false,
          }));
        },
      }),
      {
        name: "pinnacle-web/user",
      }
    )
  )
);
